export interface Job {
  id: string;
  title: string;
  location: string;
  type: string;
  department: string;
  description: string;
  responsibilities: string[];
  requirements: string[];
  qualifications: string[];
  benefits: string[];
  salaryRange?: string;
  applyUrl: string;
  postedDate: string;
  closingDate?: string;
}

export const jobs: Job[] = [
  {
    id: "research-scientist",
    title: "Research Scientist",
    location: "Remote",
    type: "Voluntary",
    department: "Research",
    description: "Join our world-class research team to advance the frontiers of artificial intelligence. You'll work on cutting-edge projects that push the boundaries of what's possible in AI, contributing to publications and developing novel algorithms that will shape the future of technology. This is a voluntary position offering equity participation in groundbreaking AI research.",
    responsibilities: [
      "Conduct original research in machine learning, deep learning, and artificial intelligence",
      "Publish high-quality research papers in top-tier conferences and journals",
      "Collaborate with cross-functional teams to translate research into practical applications",
      "Mentor junior researchers and contribute to the research community",
      "Present findings at international conferences and workshops",
      "Lead research initiatives and manage research projects from conception to completion",
      "Stay current with the latest developments in AI research and identify new research opportunities"
    ],
    requirements: [
      "Ph.D. in Computer Science, Mathematics, Physics, or related field",
      "Strong publication record in ML/AI with papers in top venues (NeurIPS, ICML, ICLR, etc.)",
      "Experience with deep learning frameworks (PyTorch, TensorFlow)",
      "Proficiency in Python and experience with scientific computing libraries",
      "Strong mathematical background in linear algebra, statistics, and optimization"
    ],
    qualifications: [
      "5+ years of research experience in academia or industry",
      "Experience with large-scale machine learning systems",
      "Knowledge of distributed computing and cloud platforms",
      "Strong communication skills and ability to present complex ideas clearly",
      "Experience mentoring students or junior researchers",
      "Track record of successful collaboration with multidisciplinary teams",
      "Passion for advancing AI research and contributing to open science"
    ],
    benefits: [
      "Equity participation in Huxley Research",
      "Flexible remote working arrangement",
      "Access to state-of-the-art computing resources and datasets",
      "Annual research budget for conferences and publications",
      "Opportunity to work with world-class researchers",
      "Contribution to cutting-edge AI research with global impact",
      "Professional development and networking opportunities",
      "Open research publication and intellectual property sharing"
    ],
    salaryRange: "Equity-based compensation",
    applyUrl: "mailto:careers@noemares.com?subject=Research%20Scientist%20Application",
    postedDate: "2024-03-15",
    closingDate: "2024-12-31"
  },
  {
    id: "software-engineer",
    title: "Software Engineer",
    location: "Remote",
    type: "Voluntary",
    department: "Engineering",
    description: "Build the robust, scalable infrastructure that powers our AI systems. You'll work on distributed systems that handle massive datasets and deploy machine learning models at scale, ensuring our platforms can support researchers and users worldwide. This voluntary position offers equity participation in revolutionary AI infrastructure development.",
    responsibilities: [
      "Design and implement scalable backend systems and APIs",
      "Build and maintain distributed systems for ML model training and inference",
      "Optimize system performance and ensure high availability",
      "Collaborate with ML engineers to deploy models in production",
      "Implement monitoring, logging, and alerting systems",
      "Write clean, maintainable code with comprehensive testing",
      "Participate in code reviews and contribute to engineering best practices",
      "Contribute to open-source infrastructure projects"
    ],
    requirements: [
      "5+ years of software engineering experience",
      "Expertise in distributed systems and microservices architecture",
      "Strong programming skills in Python, Go, or C++",
      "Experience with cloud platforms (AWS, GCP, or Azure)",
      "Knowledge of containerization technologies (Docker, Kubernetes)"
    ],
    qualifications: [
      "Bachelor's or Master's degree in Computer Science or related field",
      "Experience with database design and optimization (SQL and NoSQL)",
      "Familiarity with machine learning infrastructure and MLOps",
      "Knowledge of streaming data processing systems",
      "Experience with CI/CD pipelines and DevOps practices",
      "Strong problem-solving skills and attention to detail",
      "Commitment to open-source development and collaboration"
    ],
    benefits: [
      "Equity stake in Huxley Research",
      "Fully remote work environment with flexible hours",
      "Access to cutting-edge technology and cloud resources",
      "Opportunity to build infrastructure used by AI researchers globally",
      "Collaboration with leading AI researchers and engineers",
      "Professional development through challenging technical problems",
      "Contribution to open-source AI infrastructure",
      "Networking opportunities within the AI research community"
    ],
    salaryRange: "Equity-based compensation",
    applyUrl: "mailto:careers@noemares.com?subject=Software%20Engineer%20Application",
    postedDate: "2024-03-10",
    closingDate: "2024-12-31"
  },
  {
    id: "ml-engineer",
    title: "ML Engineer",
    location: "Remote",
    type: "Voluntary",
    department: "Machine Learning",
    description: "Develop and deploy machine learning models at scale. You'll bridge the gap between research and production, implementing state-of-the-art algorithms and ensuring they perform reliably in real-world applications serving researchers and users globally. This voluntary position offers equity participation in advanced AI system development.",
    responsibilities: [
      "Design and implement machine learning pipelines from data ingestion to model deployment",
      "Optimize model performance and reduce inference latency",
      "Build automated training and evaluation systems",
      "Monitor model performance in production and implement improvements",
      "Collaborate with research scientists to productionize new algorithms",
      "Implement A/B testing frameworks for model experimentation",
      "Ensure data quality and implement data validation pipelines",
      "Contribute to open-source ML tools and frameworks"
    ],
    requirements: [
      "MS/Ph.D. in Computer Science, Machine Learning, or related field",
      "3+ years of experience with ML model deployment and productionization",
      "Strong software engineering background with Python expertise",
      "Experience with ML frameworks (TensorFlow, PyTorch, scikit-learn)",
      "Knowledge of MLOps tools and practices"
    ],
    qualifications: [
      "Experience with cloud ML platforms (Google AI Platform, AWS SageMaker, Azure ML)",
      "Knowledge of model optimization techniques (quantization, pruning, distillation)",
      "Familiarity with big data processing frameworks (Spark, Hadoop)",
      "Experience with experiment tracking and model versioning tools",
      "Understanding of statistical analysis and experimental design",
      "Strong communication skills and ability to work in cross-functional teams",
      "Passion for democratizing access to advanced AI capabilities"
    ],
    benefits: [
      "Equity participation in groundbreaking AI research company",
      "Remote-first culture with global collaboration opportunities",
      "Access to latest ML tools and massive computing resources",
      "Work directly with cutting-edge research and models",
      "Opportunity to impact AI accessibility and democratization",
      "Professional growth through complex ML engineering challenges",
      "Contribution to open research and reproducible science",
      "Networking with top researchers and practitioners in AI"
    ],
    salaryRange: "Equity-based compensation",
    applyUrl: "mailto:careers@noemares.com?subject=ML%20Engineer%20Application",
    postedDate: "2024-03-12",
    closingDate: "2024-12-31"
  },
  {
    id: "ai-research-engineer",
    title: "AI Research Engineer",
    location: "Remote",
    type: "Voluntary",
    department: "AI & Engineering",
    description: "Bridge the gap between AI research and engineering implementation. You'll work on developing practical applications of cutting-edge AI research, building prototypes, and creating tools that enable researchers to push the boundaries of artificial intelligence.",
    responsibilities: [
      "Implement and optimize state-of-the-art AI algorithms and models",
      "Develop research prototypes and proof-of-concept systems",
      "Create tools and frameworks for AI researchers",
      "Collaborate with research scientists to translate ideas into working systems",
      "Contribute to open-source AI libraries and frameworks",
      "Benchmark and evaluate AI model performance",
      "Design experiments and run large-scale AI training jobs"
    ],
    requirements: [
      "Master's degree in Computer Science, AI, or related field",
      "3+ years of experience in AI/ML engineering",
      "Strong programming skills in Python and experience with AI frameworks",
      "Experience with GPU computing and distributed training",
      "Knowledge of transformer architectures and modern AI techniques"
    ],
    qualifications: [
      "Experience with research implementation and reproducibility",
      "Knowledge of computer vision, NLP, or multimodal AI",
      "Familiarity with cloud computing platforms and MLOps",
      "Strong mathematical background in machine learning",
      "Experience with version control and collaborative development",
      "Passion for advancing AI research through engineering excellence"
    ],
    benefits: [
      "Equity participation in breakthrough AI research",
      "Work directly with cutting-edge AI models and techniques",
      "Remote-first environment with flexible hours",
      "Access to massive computing resources and datasets",
      "Collaboration with world-class AI researchers",
      "Opportunity to shape the future of AI tooling and infrastructure",
      "Professional growth in the rapidly evolving AI field"
    ],
    salaryRange: "Equity-based compensation",
    applyUrl: "mailto:careers@noemares.com?subject=AI%20Research%20Engineer%20Application",
    postedDate: "2024-03-20",
    closingDate: "2024-12-31"
  },
  {
    id: "marketing-specialist",
    title: "Marketing Specialist",
    location: "Remote",
    type: "Voluntary",
    department: "Marketing",
    description: "Help communicate our groundbreaking AI research to the world. You'll develop marketing strategies, create compelling content, and build our brand presence in the AI research community and beyond.",
    responsibilities: [
      "Develop and execute marketing strategies for AI research initiatives",
      "Create compelling content about complex AI research for various audiences",
      "Manage social media presence and community engagement",
      "Coordinate with research teams to communicate findings effectively",
      "Organize virtual events, webinars, and conference presentations",
      "Build relationships with AI research community and media",
      "Track and analyze marketing performance metrics"
    ],
    requirements: [
      "Bachelor's degree in Marketing, Communications, or related field",
      "3+ years of experience in technology or research marketing",
      "Strong writing and communication skills",
      "Understanding of AI/ML concepts and ability to explain complex topics",
      "Experience with digital marketing tools and social media platforms"
    ],
    qualifications: [
      "Experience marketing to technical or academic audiences",
      "Knowledge of AI research landscape and key conferences",
      "Graphic design skills and experience with content creation tools",
      "Experience with email marketing and marketing automation",
      "Strong analytical skills and data-driven approach",
      "Passion for democratizing AI knowledge and research"
    ],
    benefits: [
      "Equity stake in revolutionary AI research company",
      "Opportunity to shape how cutting-edge AI research reaches the world",
      "Remote work with international team collaboration",
      "Access to groundbreaking research for content creation",
      "Professional development in AI and technology marketing",
      "Networking opportunities within the global AI community",
      "Creative freedom to experiment with innovative marketing approaches"
    ],
    salaryRange: "Equity-based compensation",
    applyUrl: "mailto:careers@noemares.com?subject=Marketing%20Specialist%20Application",
    postedDate: "2024-03-18",
    closingDate: "2024-12-31"
  },
  {
    id: "product-manager",
    title: "Product Manager",
    location: "Remote",
    type: "Voluntary",
    department: "Product",
    description: "Guide the development of AI products that will transform how people interact with artificial intelligence. You'll work closely with researchers and engineers to turn breakthrough research into practical, impactful products.",
    responsibilities: [
      "Define product strategy and roadmap for AI research applications",
      "Collaborate with researchers to identify commercialization opportunities",
      "Gather and analyze user feedback and market requirements",
      "Coordinate cross-functional teams to deliver AI products",
      "Develop go-to-market strategies for AI tools and platforms",
      "Monitor product performance and iterate based on user data",
      "Communicate product vision to stakeholders and the community"
    ],
    requirements: [
      "Bachelor's degree in Engineering, Computer Science, or related field",
      "5+ years of product management experience, preferably in AI/ML",
      "Strong understanding of AI technologies and their applications",
      "Experience with product development lifecycle and agile methodologies",
      "Excellent analytical and problem-solving skills"
    ],
    qualifications: [
      "MBA or advanced degree in business or technology",
      "Experience launching AI/ML products in research or commercial settings",
      "Knowledge of user experience design and research methods",
      "Strong leadership and communication skills",
      "Experience with data analysis and product metrics",
      "Passion for making AI research accessible and impactful"
    ],
    benefits: [
      "Equity participation in transformative AI product development",
      "Opportunity to shape the future of AI product experiences",
      "Remote-first culture with global impact potential",
      "Access to cutting-edge AI research for product innovation",
      "Collaboration with leading researchers and engineers",
      "Professional growth in AI product management",
      "Influence on how AI research translates to real-world impact"
    ],
    salaryRange: "Equity-based compensation",
    applyUrl: "mailto:careers@noemares.com?subject=Product%20Manager%20Application",
    postedDate: "2024-03-25",
    closingDate: "2024-12-31"
  },
  {
    id: "operations-coordinator",
    title: "Operations Coordinator",
    location: "Remote",
    type: "Voluntary",
    department: "Operations",
    description: "Help streamline our research operations and ensure our team can focus on breakthrough AI research. You'll manage administrative processes, coordinate between teams, and optimize our operational efficiency.",
    responsibilities: [
      "Coordinate research project timelines and deliverables",
      "Manage administrative processes and documentation",
      "Facilitate communication between distributed research teams",
      "Organize virtual meetings, workshops, and research presentations",
      "Support grant applications and research funding initiatives",
      "Maintain research databases and knowledge management systems",
      "Assist with partnership coordination and external collaborations"
    ],
    requirements: [
      "Bachelor's degree in Business Administration, Operations, or related field",
      "3+ years of experience in operations or project coordination",
      "Strong organizational and time management skills",
      "Experience with remote team coordination and collaboration tools",
      "Excellent communication and interpersonal skills"
    ],
    qualifications: [
      "Experience in research or academic operations",
      "Knowledge of project management methodologies",
      "Familiarity with research funding processes and grant management",
      "Experience with database management and documentation systems",
      "Strong attention to detail and process improvement mindset",
      "Passion for supporting groundbreaking AI research"
    ],
    benefits: [
      "Equity stake in pioneering AI research organization",
      "Opportunity to support world-changing AI research initiatives",
      "Fully remote work environment with flexible scheduling",
      "Exposure to cutting-edge AI research and development",
      "Professional development in research operations",
      "Collaboration with international team of researchers",
      "Direct impact on the efficiency and success of AI research projects"
    ],
    salaryRange: "Equity-based compensation",
    applyUrl: "mailto:careers@noemares.com?subject=Operations%20Coordinator%20Application",
    postedDate: "2024-03-22",
    closingDate: "2024-12-31"
  },
  {
    id: "legal-counsel",
    title: "Legal Counsel",
    location: "Remote",
    type: "Voluntary",
    department: "Legal",
    description: "Help navigate the complex legal landscape of AI research and development. You'll provide legal guidance on AI ethics, intellectual property, data privacy, and regulatory compliance while supporting our mission to advance responsible AI research.",
    responsibilities: [
      "Provide legal guidance on AI research ethics and compliance",
      "Review and draft contracts, partnerships, and research agreements",
      "Advise on intellectual property strategy and patent applications",
      "Ensure compliance with data privacy regulations (GDPR, CCPA, etc.)",
      "Support AI safety and ethics initiatives with legal framework",
      "Monitor regulatory developments in AI and emerging technologies",
      "Assist with corporate governance and legal documentation"
    ],
    requirements: [
      "J.D. from accredited law school and bar admission",
      "5+ years of legal experience, preferably in technology or IP law",
      "Knowledge of AI, machine learning, and emerging technology legal issues",
      "Experience with intellectual property, data privacy, and tech contracts",
      "Strong research, writing, and analytical skills"
    ],
    qualifications: [
      "Experience with AI ethics, algorithmic bias, and responsible AI frameworks",
      "Knowledge of international data privacy and AI regulations",
      "Experience in academic or research institution legal counsel",
      "Background in patent law and technology transfer",
      "Understanding of open source licensing and collaborative research",
      "Passion for advancing responsible AI development"
    ],
    benefits: [
      "Equity participation in groundbreaking AI research company",
      "Opportunity to shape legal frameworks for responsible AI",
      "Remote work with flexible hours and global collaboration",
      "Exposure to cutting-edge AI research and development",
      "Professional development in emerging technology law",
      "Collaboration with world-class researchers and technologists",
      "Direct impact on the responsible development of AI technology"
    ],
    salaryRange: "Equity-based compensation",
    applyUrl: "mailto:careers@noemares.com?subject=Legal%20Counsel%20Application",
    postedDate: "2024-03-28",
    closingDate: "2024-12-31"
  },
  {
    id: "compliance-specialist",
    title: "Compliance Specialist",
    location: "Remote",
    type: "Voluntary",
    department: "Legal",
    description: "Ensure our AI research and development activities meet the highest standards of compliance and ethical conduct. You'll develop compliance frameworks, monitor regulatory changes, and help implement best practices for responsible AI research.",
    responsibilities: [
      "Develop and implement AI ethics and compliance frameworks",
      "Monitor and analyze AI regulatory developments globally",
      "Conduct compliance audits and risk assessments",
      "Create training materials on AI ethics and compliance",
      "Collaborate with research teams on responsible AI practices",
      "Maintain compliance documentation and reporting systems",
      "Support data governance and privacy compliance initiatives"
    ],
    requirements: [
      "Bachelor's degree in Law, Business, or related field",
      "3+ years of experience in compliance, preferably in technology",
      "Knowledge of AI ethics, algorithmic fairness, and responsible AI",
      "Understanding of data privacy regulations and compliance frameworks",
      "Strong attention to detail and analytical skills"
    ],
    qualifications: [
      "Certification in compliance (CAMS, CRCM, or similar)",
      "Experience with AI governance and ethics committees",
      "Knowledge of international AI regulations and standards",
      "Experience in academic or research compliance",
      "Understanding of risk management and audit processes",
      "Passion for ethical AI development and responsible innovation"
    ],
    benefits: [
      "Equity stake in responsible AI research organization",
      "Opportunity to define compliance standards for AI research",
      "Remote-first environment with international team",
      "Access to cutting-edge AI research for compliance assessment",
      "Professional growth in AI ethics and compliance field",
      "Networking opportunities in AI governance community",
      "Direct contribution to responsible AI development"
    ],
    salaryRange: "Equity-based compensation",
    applyUrl: "mailto:careers@noemares.com?subject=Compliance%20Specialist%20Application",
    postedDate: "2024-03-30",
    closingDate: "2024-12-31"
  }
];

export function getJobById(id: string): Job | undefined {
  return jobs.find(job => job.id === id);
}

export function getAllJobs(): Job[] {
  return jobs;
}

export function getUniqueDepartments(): string[] {
  return [...new Set(jobs.map(job => job.department))].sort();
}

export function getJobsByDepartment(department: string): Job[] {
  return jobs.filter(job => job.department === department);
}
